import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';

const Login = ({ onLogin }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [themeMode, setThemeMode] = useState('dark');
    const navigate = useNavigate();

    // Validate form inputs
    const validateForm = () => {
        if (!email || !password) {
            setError('Both email and password are required');
            return false;
        }
        return true;
    };

    const handleLogin = async (e) => {
        e.preventDefault();
    
        if (!validateForm()) return;
    
        setLoading(true);
        setError(null);  // Clear any previous error messages
    
        try {
            const response = await axios.post('https://api.dmaverix.com/api/v2/auth/login', { email, password });
            console.log(response); // Log the response to see its structure
    
            const { data } = response;
    
            if (data.success) {
                localStorage.setItem('token', data.token);
                localStorage.setItem('user', JSON.stringify(data.user));
                onLogin(data.user, data.token);
                navigate('/dashboard');
            } else {
                // Handle different types of error messages
                if (data.message) {
                    console.log("Backend error message:", data.message);  // Log the backend error message
                    // If the backend returns "Email not registered"
                    if (data.message === 'Email not registered') {
                        setError('Email is not registered. Please check your email or sign up.');
                    } 
                    // If the backend returns "Incorrect password"
                    else if (data.message === 'Incorrect password') {
                        setError('Incorrect password. Please try again.');
                    }
                    // Handle other error messages
                    else if (data.message === 'Invalid credentials') {
                        setError('Wrong email or password. Please check your credentials.');
                    } else {
                        setError('Login failed. Please try again later.');
                    }
                } else {
                    // If no specific message is returned, show a generic error
                    setError('Login failed. Please try again later.');
                }
            }
        } catch (err) {
            console.error("Request failed:", err);  // Log the full error
            if (err.response) {
                setError(err.response.data.message || 'An error occurred. Please try again later.');
            } else {
                setError('An error occurred. Please try again later.');
            }
        } finally {
            setLoading(false);
        }
    };
    

    useEffect(() => {
        const savedThemeMode = localStorage.getItem('themeMode') || 'dark';
        setThemeMode(savedThemeMode);
    }, []);

    const toggleTheme = () => {
        const newMode = themeMode === 'dark' ? 'light' : 'dark';
        setThemeMode(newMode);
        localStorage.setItem('themeMode', newMode);
    };

    const theme = createTheme({
        palette: {
            mode: themeMode,
        },
    });

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    background: themeMode === 'light'
                        ? 'linear-gradient(135deg, #975cd7, #5a8fea)'
                        : 'linear-gradient(135deg, #121212, #333)',
                    position: 'relative',
                }}
            >
                <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
                    <CircularProgress color="inherit" />
                </Backdrop>

                {/* Logo */}
                <div style={{
                    position: 'absolute',
                    top: '20px',
                    left: '20px',
                    display: 'flex',
                    alignItems: 'center',
                }}>
                    <img
                        src="/assets/logo.png"
                        alt="Logo"
                        style={{
                            width: '50px',
                            height: '50px',
                            objectFit: 'contain',
                        }}
                    />
                </div>

                {/* Dark/Light Mode Toggle */}
                <div style={{ position: 'absolute', top: 20, right: 20 }}>
                    <IconButton onClick={toggleTheme} style={{ color: '#fff' }}>
                        {themeMode === 'light' ? (
                            <DarkModeOutlinedIcon />
                        ) : (
                            <LightModeOutlinedIcon />
                        )}
                    </IconButton>
                </div>

                <div
                    style={{
                        background: themeMode === 'light'
                            ? 'rgba(255, 255, 255, 0.3)'
                            : 'rgba(0, 0, 0, 0.5)',
                        padding: '30px',
                        borderRadius: '15px',
                        boxShadow: '0 8px 32px rgba(31, 38, 135, 0.37)',
                        maxWidth: '400px',
                        width: '100%',
                        textAlign: 'center',
                    }}
                >
                    <h2 style={{ marginBottom: '20px', color: themeMode === 'light' ? '#333' : '#fff', fontWeight: 'bold' }}>Login</h2>

                    {error && <p style={{ color: 'red', fontSize: '14px' }}>{error}</p>}

                    <form onSubmit={handleLogin}>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="📧 Email"
                            required
                            style={{
                                margin: '10px 0',
                                padding: '12px',
                                width: '100%',
                                border: '1px solid #ddd',
                                borderRadius: '8px',
                                outline: 'none',
                                fontSize: '16px',
                                transition: '0.3s',
                                background: themeMode === 'light' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.2)',
                                color: themeMode === 'light' ? '#333' : '#fff',
                            }}
                        />
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="🔒 Password"
                            required
                            style={{
                                margin: '10px 0',
                                padding: '12px',
                                width: '100%',
                                border: '1px solid #ddd',
                                borderRadius: '8px',
                                outline: 'none',
                                fontSize: '16px',
                                transition: '0.3s',
                                background: themeMode === 'light' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.2)',
                                color: themeMode === 'light' ? '#333' : '#fff',
                            }}
                        />
                        <button
                            type="submit"
                            disabled={loading}
                            style={{
                                marginTop: '10px',
                                padding: '12px',
                                background: themeMode === 'light'
                                    ? 'linear-gradient(90deg, #975cd7, #5a8fea)'
                                    : 'linear-gradient(90deg, #333, #121212)',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '16px',
                                fontWeight: 'bold',
                                transition: '0.3s ease',
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                            }}
                        >
                            {loading ? 'Logging in...' : 'Login'}
                        </button>
                        <p>
                            <Link
                                to="/forgot-password"
                                style={{
                                    color: themeMode === 'light' ? '#444444' : '#ffffff',
                                    textDecoration: 'none',
                                    fontWeight: 'bold',
                                }}
                            >
                                Forgot Password?
                            </Link>
                        </p>
                    </form>
                    <p style={{ marginTop: '15px', color: themeMode === 'light' ? '#D7127A' : '#E57373' }}>
                        Don’t have an account?{' '}
                        <Link
                            to="/register"
                            style={{
                                color: themeMode === 'light' ? '#444444' : '#ffffff',
                                textDecoration: 'none',
                                fontWeight: 'bold',
                            }}
                        >
                            Register here
                        </Link>
                    </p>
                    {error && <p style={{ color: 'red', marginTop: '10px' }}>{error}</p>}
                </div>
            </div>
        </ThemeProvider>
    );
};

export default Login;
